import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import RequiredMark from "../components/RequiredMark";
import {
  AccessGroup,
  AccessPoint,
  ACCESS_GROUP_FIELD_TYPES,
  GROUP_PHOTOS,
  GROUP_ROLES,
  makeEmptyAccessGroupField,
  WEEK_DAYS,
} from "../types";
import { passtrackColors } from "../theme";

interface AccessGroupsStepProps {
  groups: AccessGroup[];
  selectedGroupId: string | null;
  onSelectGroup: (id: string) => void;
  onAddGroup: () => void;
  onUpdateGroup: (id: string, patch: Partial<AccessGroup>) => void;
  onRemoveGroup: (id: string) => void;
  accessPoints: AccessPoint[];
  hasScheduleRestrictions: boolean;
  onToggleScheduleRestrictions: (enabled: boolean) => void;
  hasRequirementsPermissions: boolean;
}

const isAllDayAccess = (g: AccessGroup) =>
  g.from === "00:00" && g.to === "23:59";

const isAllDaysActive = (days: number[]) =>
  days.length === 7 && days.every((day) => day === 1);

const hoursOf = (g: AccessGroup) =>
  g.from === "00:00" && g.to === "23:59" ? "24 h" : `${g.from} – ${g.to}`;

const photoCount = (g: AccessGroup) =>
  Object.values(g.photos).filter(Boolean).length;

export default function AccessGroupsStep({
  groups,
  selectedGroupId,
  onSelectGroup,
  onAddGroup,
  onUpdateGroup,
  onRemoveGroup,
  accessPoints,
  hasScheduleRestrictions,
  onToggleScheduleRestrictions,
  hasRequirementsPermissions,
}: AccessGroupsStepProps) {
  const group = groups.find((g) => g.id === selectedGroupId) ?? groups[0] ?? null;

  useEffect(() => {
    const anyExitRestricted = groups.some((item) => item.exitRestricted);
    if (anyExitRestricted && !hasScheduleRestrictions) {
      onToggleScheduleRestrictions(true);
    }
  }, [groups, hasScheduleRestrictions, onToggleScheduleRestrictions]);

  const handleExitRestrictedChange = (checked: boolean) => {
    if (!group) return;
    onUpdateGroup(group.id, { exitRestricted: checked });
    if (checked) {
      onToggleScheduleRestrictions(true);
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "352px minmax(0,1fr)" },
        gap: 3,
        alignItems: "start",
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {groups.length === 0 && (
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted, mb: 1 }}>
              Aún no hay grupos de acceso.
            </Typography>
            <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
              Puedes configurar aquí o posterior en la aplicación.
            </Typography>
          </Card>
        )}
        {groups.length > 0 && (
          <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted, px: 0.5 }}>
            Puedes configurar aquí o posterior en la aplicación. Si das de alta
            un grupo, completa los campos obligatorios.
          </Typography>
        )}
        {groups.map((g) => (
          <Box
            key={g.id}
            onClick={() => onSelectGroup(g.id)}
            sx={{
              textAlign: "left",
              background: "#fff",
              border: `1.5px solid ${g.id === group?.id ? passtrackColors.blue : passtrackColors.borderSubtle}`,
              borderRadius: 2.5,
              p: 2.25,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.25 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 15, color: passtrackColors.ink, minWidth: 0 }}>
                {g.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: passtrackColors.blue900, background: passtrackColors.cloud, borderRadius: 99, px: 1, py: 0.4, whiteSpace: "nowrap" }}>
                  {hoursOf(g)}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  aria-label={`Eliminar ${g.name || "grupo"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveGroup(g.id);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0.5,
                    background: "none",
                    border: "none",
                    color: passtrackColors.red,
                    cursor: "pointer",
                    borderRadius: 1,
                    "&:hover": {
                      background: "rgba(228, 80, 31, 0.08)",
                    },
                  }}
                >
                  <Trash2 size={14} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {g.roles.map((r) => {
                const meta = GROUP_ROLES.find((x) => x.key === r);
                return (
                  <Typography
                    key={r}
                    sx={{ fontSize: 11, fontWeight: 600, color: passtrackColors.green700, background: passtrackColors.greenMist, borderRadius: 99, px: 1, py: 0.4 }}
                  >
                    {meta?.label}
                  </Typography>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: passtrackColors.textMuted }}>
              {g.points.length} punto(s) · {photoCount(g)} fotografía(s)
              {hasRequirementsPermissions
                ? ` · ${g.fields.length} campo(s) extra`
                : ""}
              {g.exitRestricted ? " · salida restringida" : ""}
            </Typography>
          </Box>
        ))}
        <Button variant="text" onClick={onAddGroup} fullWidth>
          Nuevo grupo de acceso
        </Button>
      </Box>

      {group && (
        <Card
          variant="outlined"
          sx={{
            p: 3.5,
            borderRadius: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 3.25,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <TextField
              label="Nombre del grupo"
              size="small"
              fullWidth
              value={group.name}
              onChange={(e) => onUpdateGroup(group.id, { name: e.target.value })}
              required
            />
            <Box
              component="button"
              onClick={() => onRemoveGroup(group.id)}
              sx={{ mt: 0.75, background: "none", border: "none", color: passtrackColors.red, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Eliminar grupo
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography fontWeight={600} fontSize={14} color={passtrackColors.ink}>
                Puntos de acceso
                <RequiredMark />
              </Typography>
              {accessPoints.map((p) => {
                const on = group.points.includes(p.id);
                return (
                  <Box
                    key={p.id}
                    component="button"
                    onClick={() =>
                      onUpdateGroup(group.id, {
                        points: on
                          ? group.points.filter((x) => x !== p.id)
                          : [...group.points, p.id],
                      })
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      textAlign: "left",
                      background: on ? passtrackColors.cloud : "#fff",
                      border: `1px solid ${on ? passtrackColors.blue : passtrackColors.borderSubtle}`,
                      borderRadius: 1.5,
                      p: 1.25,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: passtrackColors.ink }}>
                      {p.nombre || "Punto sin nombre"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography fontWeight={600} fontSize={14} color={passtrackColors.ink}>
                Roles a los que aplica
                <RequiredMark />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {GROUP_ROLES.map((r) => {
                  const on = group.roles.includes(r.key);
                  return (
                    <Box
                      key={r.key}
                      component="button"
                      onClick={() =>
                        onUpdateGroup(group.id, {
                          roles: on
                            ? group.roles.filter((x) => x !== r.key)
                            : [...group.roles, r.key],
                        })
                      }
                      sx={{
                        background: on ? passtrackColors.greenMist : "#fff",
                        border: `1px solid ${on ? passtrackColors.green : passtrackColors.borderDefault}`,
                        color: on ? passtrackColors.green700 : passtrackColors.textMuted,
                        borderRadius: 99,
                        px: 1.75,
                        py: 1,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      {r.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, borderTop: `1px solid ${passtrackColors.borderSubtle}`, pt: 2.5, minWidth: 0 }}>
            <Typography fontWeight={600} fontSize={14} color={passtrackColors.ink}>
              Ventana horaria
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 2,
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  background: passtrackColors.mist,
                  border: `1px solid ${passtrackColors.borderSubtle}`,
                  borderRadius: 1.5,
                  p: 2,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, fontSize: 13, color: passtrackColors.green700 }}
                >
                  Entrada
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isAllDayAccess(group)}
                      onChange={(_, checked) =>
                        onUpdateGroup(
                          group.id,
                          checked
                            ? { from: "00:00", to: "23:59" }
                            : { from: "08:00", to: "20:00" },
                        )
                      }
                    />
                  }
                  label="Permitir acceso todo el día"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                      color: passtrackColors.ink,
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label="Desde"
                    type="time"
                    size="small"
                    fullWidth
                    value={group.from}
                    disabled={isAllDayAccess(group)}
                    onChange={(e) =>
                      onUpdateGroup(group.id, { from: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Hasta"
                    type="time"
                    size="small"
                    fullWidth
                    value={group.to}
                    disabled={isAllDayAccess(group)}
                    onChange={(e) =>
                      onUpdateGroup(group.id, { to: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  background: group.exitRestricted
                    ? passtrackColors.cloud
                    : passtrackColors.mist,
                  border: `1px solid ${
                    group.exitRestricted
                      ? passtrackColors.blue200
                      : passtrackColors.borderSubtle
                  }`,
                  borderRadius: 1.5,
                  p: 2,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: passtrackColors.blue900,
                  }}
                >
                  Salida
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={group.exitRestricted}
                      onChange={(_, checked) =>
                        handleExitRestrictedChange(checked)
                      }
                    />
                  }
                  label="Restringir horario de salida en este grupo"
                  sx={{
                    m: 0,
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                      color: passtrackColors.ink,
                      pt: 0.25,
                    },
                  }}
                />

                {group.exitRestricted && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      label="Desde"
                      type="time"
                      size="small"
                      fullWidth
                      value={group.exitFrom}
                      onChange={(e) =>
                        onUpdateGroup(group.id, {
                          exitFrom: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Hasta"
                      type="time"
                      size="small"
                      fullWidth
                      value={group.exitTo}
                      onChange={(e) =>
                        onUpdateGroup(group.id, {
                          exitTo: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: passtrackColors.graphite }}
              >
                Días activos
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isAllDaysActive(group.days)}
                    onChange={(_, checked) =>
                      onUpdateGroup(
                        group.id,
                        { days: checked ? [1, 1, 1, 1, 1, 1, 1] : [0, 0, 0, 0, 0, 0, 0] },
                      )
                    }
                  />
                }
                label="Todos los días"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 12,
                    fontWeight: 600,
                    color: passtrackColors.textMuted,
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                {WEEK_DAYS.map((label, i) => {
                  const on = !!group.days[i];
                  return (
                    <Box
                      key={i}
                      component="button"
                      onClick={() =>
                        onUpdateGroup(group.id, {
                          days: group.days.map((v, j) => (j === i ? (v ? 0 : 1) : v)),
                        })
                      }
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: on ? passtrackColors.blue : "#fff",
                        border: `1px solid ${on ? passtrackColors.blue : passtrackColors.borderDefault}`,
                        color: on ? "#fff" : passtrackColors.textMuted,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, borderTop: `1px solid ${passtrackColors.borderSubtle}`, pt: 2.5 }}>
            <Typography fontWeight={600} fontSize={14} color={passtrackColors.ink}>
              Fotografías requeridas al registrar
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
              {GROUP_PHOTOS.map((p) => {
                const on = !!group.photos[p.key];
                return (
                  <Box
                    key={p.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      background: on ? passtrackColors.greenMist : passtrackColors.mist,
                      border: `1px solid ${on ? passtrackColors.green200 : passtrackColors.borderSubtle}`,
                      borderRadius: 1.5,
                      px: 1.75,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: passtrackColors.ink }}>
                      {p.label}
                    </Typography>
                    <Switch
                      size="small"
                      checked={on}
                      onChange={(_, checked) =>
                        onUpdateGroup(group.id, {
                          photos: { ...group.photos, [p.key]: checked },
                        })
                      }
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {hasRequirementsPermissions ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                borderTop: `1px solid ${passtrackColors.borderSubtle}`,
                pt: 2.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography fontWeight={600} fontSize={14} color={passtrackColors.ink}>
                  Campos personalizados del registro
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    onUpdateGroup(group.id, {
                      fields: [...group.fields, makeEmptyAccessGroupField()],
                    })
                  }
                >
                  Agregar campo
                </Button>
              </Box>
              {group.fields.map((f) => (
              <Box
                key={f.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  background: passtrackColors.mist,
                  border: `1px solid ${passtrackColors.borderSubtle}`,
                  borderRadius: 1.5,
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "160px minmax(0,1fr) auto",
                    },
                    gap: 1.25,
                    alignItems: "center",
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel id={`field-type-${f.id}`}>Tipo</InputLabel>
                    <Select
                      labelId={`field-type-${f.id}`}
                      label="Tipo"
                      value={f.type}
                      onChange={(e) =>
                        onUpdateGroup(group.id, {
                          fields: group.fields.map((x) =>
                            x.id === f.id
                              ? {
                                  ...x,
                                  type: e.target.value as typeof f.type,
                                  options:
                                    e.target.value === "selector"
                                      ? x.options
                                      : "",
                                }
                              : x,
                          ),
                        })
                      }
                    >
                      {ACCESS_GROUP_FIELD_TYPES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Nombre del campo"
                    placeholder="Ej. CURP, Vehículo"
                    value={f.label}
                    onChange={(e) =>
                      onUpdateGroup(group.id, {
                        fields: group.fields.map((x) =>
                          x.id === f.id ? { ...x, label: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      onUpdateGroup(group.id, {
                        fields: group.fields.filter((x) => x.id !== f.id),
                      })
                    }
                    sx={{
                      background: "none",
                      border: "none",
                      color: passtrackColors.textMuted,
                      cursor: "pointer",
                      alignSelf: "center",
                      fontSize: 18,
                      lineHeight: 1,
                      p: 0.5,
                    }}
                  >
                    ×
                  </Box>
                </Box>
                {f.type === "selector" ? (
                  <TextField
                    size="small"
                    fullWidth
                    label="Opciones del selector"
                    placeholder="Ej. automóvil, motocicleta, bicicleta"
                    value={f.options}
                    onChange={(e) =>
                      onUpdateGroup(group.id, {
                        fields: group.fields.map((x) =>
                          x.id === f.id ? { ...x, options: e.target.value } : x,
                        ),
                      })
                    }
                    helperText="Separa cada opción con una coma."
                  />
                ) : null}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={f.required}
                        onChange={(_, checked) =>
                          onUpdateGroup(group.id, {
                            fields: group.fields.map((x) =>
                              x.id === f.id ? { ...x, required: checked } : x,
                            ),
                          })
                        }
                      />
                    }
                    label="Campo requerido"
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: 13,
                        color: passtrackColors.ink,
                      },
                    }}
                  />
                </Box>
              </Box>
            ))}
            </Box>
          ) : null}
        </Card>
      )}
    </Box>
  );
}
